import { AccountBase } from '@/models/AccountViewModel';
import {
  BalanceDataInterface,
  AccountCategory,
} from '@/interfaces/balanceSheet';

export type GroupByOption = 'classification' | 'institution' | 'account';

export interface AccountFilters {
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
}

/**
 * Filters accounts based on excluded values
 * @param accounts - Array of accounts to filter
 * @param filters - Filter configuration with excluded IDs
 * @returns Filtered array of accounts
 */
export function filterAccounts(
  accounts: AccountBase[],
  filters: AccountFilters,
): AccountBase[] {
  if (
    !filters.excludedAccounts?.length &&
    !filters.excludedCategories?.length &&
    !filters.excludedAccountTypes?.length &&
    !filters.excludedInstitutions?.length
  ) {
    return accounts;
  }

  return accounts.filter((account) => {
    // Exclude if account ID is in excluded list
    if (filters.excludedAccounts?.includes(account.AccountId)) {
      return false;
    }

    // Exclude if classification ID is in excluded list
    if (filters.excludedCategories?.includes(account.ClassificationId)) {
      return false;
    }

    // Exclude if account type is in excluded list
    if (filters.excludedAccountTypes?.includes(account.AccountType)) {
      return false;
    }

    // Exclude if institution ID is in excluded list
    if (filters.excludedInstitutions?.includes(account.InstitutionId)) {
      return false;
    }

    return true;
  });
}

function getGroupKey(
  account: AccountBase,
  groupBy: GroupByOption,
): { key: string; name: string } {
  switch (groupBy) {
    case 'institution':
      return {
        key: account.InstitutionId.toString(),
        name: account.InstitutionName,
      };
    case 'account':
      return { key: account.AccountId.toString(), name: account.Name };
    default:
      return {
        key: account.ClassificationId.toString(),
        name: account.ClassificationName,
      };
  }
}

export function accountsToBalanceData(
  accounts: AccountBase[],
  groupBy: GroupByOption = 'classification',
): BalanceDataInterface {
  const assetMap = new Map<string, AccountCategory>();
  const liabilityMap = new Map<string, AccountCategory>();
  let assetTotal = 0;
  let liabilityTotal = 0;

  accounts.forEach((account) => {
    const map = account.IsAsset ? assetMap : liabilityMap;
    const { key, name } = getGroupKey(account, groupBy);

    if (!map.has(key)) {
      map.set(key, { name, accounts: [], total: 0 });
    }

    const category = map.get(key)!;
    category.accounts.push(account);
    category.total += account.Balance;

    if (account.IsAsset) {
      assetTotal += account.Balance;
    } else {
      liabilityTotal += account.Balance;
    }
  });

  return {
    assets: { total: assetTotal, categories: Array.from(assetMap.values()) },
    liabilities: {
      total: liabilityTotal,
      categories: Array.from(liabilityMap.values()),
    },
    totalLiabilitiesAndEquity: assetTotal,
  };
}
